import React from 'react';
import { ScrubInput } from 'calc-controls';
import type { PenaltyTier } from '@abs/shared-types';
import { generateId } from '@/utils/id';
import { Button, Input } from '../shared';

export interface PenaltyTierEditorProps {
  tiers: PenaltyTier[];
  onChange: (tiers: PenaltyTier[]) => void;
}

export function PenaltyTierEditor({ tiers, onChange }: PenaltyTierEditorProps): React.ReactElement {
  const sorted = [...tiers].sort((a, b) => a.absenceThreshold - b.absenceThreshold);

  const addTier = () => {
    onChange([...tiers, {
      id: generateId(),
      absenceThreshold: 0,
      gradeDeduction: 0,
      letterGradeDrop: 0,
      description: '',
    }]);
  };

  const removeTier = (id: string) => {
    onChange(tiers.filter((t) => t.id !== id));
  };

  const updateTier = (id: string, field: keyof PenaltyTier, value: string | number) => {
    onChange(tiers.map((t) => (t.id === id ? { ...t, [field]: value } : t)));
  };

  return (
    <div className="penalty-tier-editor">
      <h4>Penalty Tiers</h4>
      {sorted.map((tier) => (
        <div key={tier.id} className="penalty-tier-editor__tier">
          <ScrubInput
            label="Absence Threshold"
            value={tier.absenceThreshold}
            onChange={(v) => updateTier(tier.id, 'absenceThreshold', v)}
            min={0}
            decimals={0}
            tooltip="Penalty absences (after grace) at which this tier activates."
          />
          <ScrubInput
            label="Grade Deduction (points)"
            value={tier.gradeDeduction}
            onChange={(v) => updateTier(tier.id, 'gradeDeduction', v)}
            decimals={0}
            tooltip="Points subtracted from the base grade when this tier activates."
          />
          <ScrubInput
            label="Letter Grade Drop"
            value={tier.letterGradeDrop}
            onChange={(v) => updateTier(tier.id, 'letterGradeDrop', v)}
            min={0}
            decimals={0}
            tooltip="Letter-grade levels dropped when this tier activates (e.g. 1 = A to B)."
          />
          <Input
            label="Description"
            value={tier.description}
            onChange={(e) => updateTier(tier.id, 'description', e.target.value)}
          />
          <Button variant="danger" size="sm" onClick={() => removeTier(tier.id)}>
            Remove
          </Button>
        </div>
      ))}
      <Button variant="secondary" size="sm" onClick={addTier}>
        + Add Tier
      </Button>
    </div>
  );
}
